/**
 * Oracle's rows of the connection form (Option C, C2). "Connect by" comes first in the Server section and decides what
 * follows: EZConnect asks Host and Port (the shared server rows) and the Service name here; a TNS alias asks the alias
 * and the TNS_ADMIN folder; a TNS descriptor asks the descriptor — neither TNS mode has a Server or Port (C0). The
 * wallet folder sits in Security; the initial schema and the debug protocol in More options.
 */
import { useState } from 'react';
import { FolderOpen, Search } from 'lucide-react';
import type { OracleConnectionStringType, OracleDebugProtocol } from './connectionFormSchema';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { runOracleServiceDiscovery, type OracleDiscoveryState } from '../utils/oracleDiscoveryClient';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { ruleValidators } from '../connection-dialog/state/fieldRuleValidators';
import { FormRow, rowLabelId } from '../connection-dialog/pane/FormRow';
import { CHECKBOX_CLASS, FieldError, SELECT_CLASS, TEXTAREA_CLASS, type ConnectionFormApi } from '../connection-dialog/fields/fieldControls';

/** The TNS descriptor / alias is what the connection dials in its mode (C0): required there, and only there. */
const TNS_CAUSES = ['onChange', 'onSubmit'] as const;

type FormProps = { form: ConnectionFormApi };

export function OracleConnectByRow({ form }: FormProps) {
  return (
    <form.Field name="oracleConnectionStringType">
      {(field: any) => (
        <FormRow row="oracleConnectionStringType" label="Connect by" htmlFor="oracleConnectionStringType">
          <select
            id="oracleConnectionStringType"
            name="oracleConnectionStringType"
            className={SELECT_CLASS}
            value={field.state.value ?? 'ezconnect'}
            onChange={(event) => field.handleChange(event.target.value as OracleConnectionStringType)}
            onBlur={field.handleBlur}
            data-testid="oracle-connection-string-type-select"
          >
            <option value="ezconnect">EZConnect (host, port and service name)</option>
            <option value="tns_alias">TNS alias</option>
            <option value="tns_descriptor">TNS descriptor</option>
          </select>
        </FormRow>
      )}
    </form.Field>
  );
}

/** What the mode dials besides the shared Host / Port rows: the service name, or the TNS alias / descriptor. */
export function OracleTargetRows({ form }: FormProps) {
  return (
    <form.Subscribe selector={(state: any) => state.values.oracleConnectionStringType ?? 'ezconnect'}>
      {(mode: OracleConnectionStringType) => (
        <>
          {mode === 'ezconnect' && <ServiceNameRow form={form} />}
          {mode === 'tns_alias' && <TnsAliasRows form={form} />}
          {mode === 'tns_descriptor' && <TnsDescriptorRow form={form} />}
        </>
      )}
    </form.Subscribe>
  );
}

function ServiceNameRow({ form }: FormProps) {
  const [discovery, setDiscovery] = useState<OracleDiscoveryState>({ status: 'idle' });

  const handleDiscover = async (applyValue: (value: string) => void, currentValue: string) => {
    const host = String(form.getFieldValue('server') ?? '');
    setDiscovery({ status: 'loading' });
    const result = await runOracleServiceDiscovery(host, form.getFieldValue('port'), currentValue);
    setDiscovery(result);
    // Auto-select if the current value is empty and exactly one service was found.
    if (result.status === 'ok' && !currentValue.trim() && result.services.length === 1) {
      applyValue(result.services[0]);
    }
  };

  return (
    <form.Field name="oracleServiceName">
      {(field: any) => (
        <FormRow row="oracleServiceName" label="Service name" htmlFor="oracleServiceName">
          <div className="flex items-center gap-2">
            <Input
              id="oracleServiceName"
              name="oracleServiceName"
              placeholder="ORCL"
              value={field.state.value ?? ''}
              onChange={(event) => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
              data-testid="oracle-service-name-input"
            />
            <Button
              type="button"
              variant="outline"
              disabled={discovery.status === 'loading'}
              onClick={() => handleDiscover((value) => field.handleChange(value), field.state.value ?? '')}
              aria-describedby={rowLabelId('oracleServiceName')}
              data-testid="oracle-discover-services-button"
              title="Discover services registered with the Oracle listener at host:port"
            >
              {discovery.status === 'loading' ? <RandomDotmSpinner size={16} /> : <Search className="h-4 w-4" />}
              Discover
            </Button>
          </div>
          <form.Field name="oracleUseSID">
            {(sid: any) => (
              <label htmlFor="oracleUseSID" className="mt-1.5 flex items-center gap-2 text-sm">
                <input
                  id="oracleUseSID"
                  name="oracleUseSID"
                  type="checkbox"
                  className={CHECKBOX_CLASS}
                  checked={sid.state.value ?? false}
                  onChange={(event) => sid.handleChange(event.target.checked)}
                  onBlur={sid.handleBlur}
                  data-testid="oracle-use-sid-checkbox"
                />
                Use SID (the name is a SID, not a service name)
              </label>
            )}
          </form.Field>
          <DiscoveryResults state={discovery} onPick={(service) => field.handleChange(service)} />
        </FormRow>
      )}
    </form.Field>
  );
}

/**
 * Settled outcome of a listener probe — error / empty / pick-a-service pills.
 * Shared with the connection-error notice's "Browse services…" fix so both
 * surfaces present discovery identically.
 */
export function DiscoveryResults({ state, onPick }: { state: OracleDiscoveryState; onPick: (service: string) => void }) {
  if (state.status === 'idle' || state.status === 'loading') return null;
  if (state.status === 'error') {
    return <p className="mt-2 text-xs text-destructive" data-testid="oracle-discover-services-error">{state.message}</p>;
  }
  if (state.status === 'empty') {
    return (
      <p className="mt-2 text-xs text-muted-foreground" data-testid="oracle-discover-services-empty">
        No well-known services registered with the listener. Enter the service name manually.
      </p>
    );
  }
  return (
    <div className="mt-2" data-testid="oracle-discover-services-results">
      <p className="mb-1 text-xs text-muted-foreground">
        Found {state.services.length} service{state.services.length === 1 ? '' : 's'}. Click to use:
      </p>
      <div className="flex flex-wrap gap-1.5">
        {state.services.map((service) => (
          <button
            key={service}
            type="button"
            onClick={() => onPick(service)}
            className="rounded-full border border-input bg-background px-2.5 py-0.5 font-mono text-xs transition-colors hover:bg-accent hover:text-accent-foreground"
            data-testid={`oracle-discover-services-pill-${service}`}
          >
            {service}
          </button>
        ))}
      </div>
    </div>
  );
}

function TnsDescriptorRow({ form }: FormProps) {
  return (
    <form.Field name="oracleTnsDescriptor" validators={ruleValidators('oracleTnsDescriptor', TNS_CAUSES)}>
      {(field: any) => (
        <FormRow row="oracleTnsDescriptor" label="TNS descriptor" htmlFor="oracleTnsDescriptor">
          <textarea
            id="oracleTnsDescriptor"
            name="oracleTnsDescriptor"
            rows={4}
            placeholder="(DESCRIPTION=(ADDRESS=(PROTOCOL=TCP)(HOST=myhost)(PORT=1521))(CONNECT_DATA=(SERVICE_NAME=myservice)))"
            className={`${TEXTAREA_CLASS} font-mono text-xs`}
            value={field.state.value ?? ''}
            onChange={(event) => field.handleChange(event.target.value)}
            onBlur={field.handleBlur}
            data-testid="oracle-tns-descriptor-input"
          />
          <FieldError meta={field.state.meta} testId="oracle-tns-descriptor-error" />
        </FormRow>
      )}
    </form.Field>
  );
}

/** A folder path with a Browse… button (described by the row's label). */
function FolderInput({ id, value, placeholder, title, testId, onChange, onBlur }: {
  id: string; value: string; placeholder: string; title: string; testId: string;
  onChange: (value: string) => void; onBlur?: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Input id={id} name={id} placeholder={placeholder} value={value} onChange={(event) => onChange(event.target.value)} onBlur={onBlur} className="font-mono" data-testid={`${testId}-input`} />
      <Button
        type="button"
        variant="outline"
        aria-describedby={rowLabelId(id)}
        onClick={async () => {
          try {
            const result = await window.electronAPI?.oracle?.selectDirectory({ title });
            if (result?.directoryPath) onChange(result.directoryPath);
          } catch (error) {
            console.error(`${title} failed:`, error);
          }
        }}
        data-testid={`${testId}-browse`}
      >
        <FolderOpen className="h-4 w-4" />
        Browse…
      </Button>
    </div>
  );
}

function TnsAliasRows({ form }: FormProps) {
  return (
    <>
      <form.Field name="oracleTnsAlias" validators={ruleValidators('oracleTnsAlias', TNS_CAUSES)}>
        {(field: any) => (
          <FormRow row="oracleTnsAlias" label="TNS alias" htmlFor="oracleTnsAlias">
            <Input
              id="oracleTnsAlias"
              name="oracleTnsAlias"
              placeholder="MYDB"
              value={field.state.value ?? ''}
              onChange={(event) => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
              data-testid="oracle-tns-alias-input"
            />
            <FieldError meta={field.state.meta} testId="oracle-tns-alias-error" />
          </FormRow>
        )}
      </form.Field>
      <form.Field name="oracleTnsAdmin">
        {(field: any) => (
          <FormRow row="oracleTnsAdmin" label="TNS_ADMIN folder" htmlFor="oracleTnsAdmin" note="optional" hint="The folder with tnsnames.ora. Empty: the TNS_ADMIN environment variable.">
            <FolderInput id="oracleTnsAdmin" value={field.state.value ?? ''} placeholder="~/oracle/network/admin" title="Select TNS Admin Directory" testId="oracle-tns-admin" onChange={field.handleChange} onBlur={field.handleBlur} />
          </FormRow>
        )}
      </form.Field>
    </>
  );
}

export function OracleWalletRow({ form }: FormProps) {
  return (
    <form.Field name="oracleWalletLocation">
      {(field: any) => (
        <FormRow row="oracleWalletLocation" label="Wallet folder" htmlFor="oracleWalletLocation" note="optional" hint="For mTLS connections, e.g. Oracle Autonomous Database (cwallet.sso / ewallet.p12).">
          <FolderInput id="oracleWalletLocation" value={field.state.value ?? ''} placeholder="~/oracle/wallet" title="Select Wallet Directory" testId="oracle-wallet-location" onChange={field.handleChange} onBlur={field.handleBlur} />
        </FormRow>
      )}
    </form.Field>
  );
}

export function OracleInitialSchemaRow({ form }: FormProps) {
  return (
    <form.Field name="oracleInitialSchema">
      {(field: any) => (
        <FormRow row="oracleInitialSchema" label="Initial schema" htmlFor="oracleInitialSchema" note="optional">
          <Input
            id="oracleInitialSchema"
            name="oracleInitialSchema"
            placeholder="Defaults to the user's schema"
            value={field.state.value ?? ''}
            onChange={(event) => field.handleChange(event.target.value)}
            onBlur={field.handleBlur}
            data-testid="oracle-initial-schema-input"
          />
        </FormRow>
      )}
    </form.Field>
  );
}

export function OracleDebugProtocolRow({ form }: FormProps) {
  return (
    <form.Field name="oracleDebugProtocol">
      {(field: any) => (
        <FormRow
          row="oracleDebugProtocol"
          label="Debug protocol"
          htmlFor="oracleDebugProtocol"
          hint="DBMS_DEBUG works out of the box. JDWP needs a network ACL but is the officially supported protocol."
        >
          <select
            id="oracleDebugProtocol"
            name="oracleDebugProtocol"
            className={SELECT_CLASS}
            value={field.state.value ?? 'dbms_debug'}
            onChange={(event) => field.handleChange(event.target.value as OracleDebugProtocol)}
            onBlur={field.handleBlur}
            data-testid="oracle-debug-protocol-select"
          >
            <option value="dbms_debug">DBMS_DEBUG (no network setup)</option>
            <option value="jdwp">JDWP (Oracle recommended)</option>
          </select>
        </FormRow>
      )}
    </form.Field>
  );
}
